import * as React from "react";
import styled from "styled-components";
import { Button, Select } from "antd";

const Container = styled.div`
  padding: 30px 20px 0 10px;
  height: 300px;
  .cameraSelect {
    width: 280px;
    margin: 10px 0;
  }
  video {
    width: 500px;
    height: 280px;
    margin-bottom: 5px;
    background: black;
  }
`;

const AudioSetting: React.FunctionComponent = () => {
  const [inputs, setInputs] = React.useState([]);
  const [outputs, setOutputs] = React.useState([]);
  const [current, setCurrent] = React.useState("default");

  const checkDevice = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const inputsDevices = devices.filter(
      (device) => device.kind === "audioinput"
    );
    const outputsDevices = devices.filter(
      (device) => device.kind === "audiooutput"
    );
    setInputs(inputsDevices);
    setOutputs(outputsDevices);

    audioRef.current!.src = require("@/assets/sample-9s.mp3");
    audioRef.current!.load();
  };

  const formatDeviceLabel = (label: string) => {
    const regex = /\(([0-9a-fA-F]{4}:[0-9a-fA-F]{4})\)/;

    if (regex.test(label)) { 
      return label.replace(regex, "").trim();
    }
    return label; 
  };

  React.useEffect(() => {
    checkDevice();
  }, []);

  const [isTestInput, setIsTestInput] = React.useState(false);

  const audioContextRef = React.useRef<AudioContext>(null);
  const analyserRef = React.useRef<AnalyserNode | null>(null);
  const dataArrayFrequencyRef = React.useRef<any | null>(null); // Only need frequency data for volume bars
  const sourceRef = React.useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameIdRef = React.useRef<number | null>(null);

  const setupAudioContext = React.useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }
    if (!analyserRef.current) {
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.8;

      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayFrequencyRef.current = new Uint8Array(bufferLength);
    }
  }, []);

  const barCount = 25;

  const setupVolumeBars = React.useCallback(() => {
    const volumeCanvas = volumeCanvasRef.current;

    const barWidth = (volumeCanvas!.width / (barCount * 2 - 1));

    if (volumeCanvas) {
      const volumeCtx = volumeCanvas.getContext('2d');
      volumeCtx!.clearRect(0, 0, volumeCanvas.width, volumeCanvas.height);
      if (volumeCtx) {
        volumeCtx.fillStyle = 'rgb(240, 240, 240)';
        for (let i = 0; i < barCount; i++) {
          const xPos = i * (barWidth * 2);
          const height = volumeCanvas.height
          volumeCtx!.fillRect(xPos, 0, barWidth, height);
        }
      } else {
        console.error('Failed to get canvas context for volume canvas.');
      }
    } else {
      console.error('Volume canvas is not available.');
    }
  }, [])

  const volumeCanvasRef = React.useRef<HTMLCanvasElement>(null); // Ref for the volume bar canvas

  const drawVolumeBars = React.useCallback(() => {
    if (!analyserRef.current || !volumeCanvasRef.current) {
      animationFrameIdRef.current = requestAnimationFrame(drawVolumeBars);
      return;
    }

    analyserRef.current.getByteFrequencyData(dataArrayFrequencyRef.current!);

    const volumeCanvas = volumeCanvasRef.current;
    const volumeCtx = volumeCanvas.getContext('2d');

    volumeCtx!.clearRect(0, 0, volumeCanvas.width, volumeCanvas.height);

    const barWidth = (volumeCanvas.width / (barCount * 2 - 1));

    let sumAmplitude = 0;
    for (let i = 0; i < dataArrayFrequencyRef.current!.length; i++) {
      sumAmplitude += dataArrayFrequencyRef.current![i];
    }
    const averageAmplitude = sumAmplitude / dataArrayFrequencyRef.current!.length;
    const normalizedAmplitude = averageAmplitude / 255;

    const count = 400 * normalizedAmplitude / barWidth;

    for (let i = 0; i < barCount; i++) {
      if (i <= count) {
        volumeCtx!.fillStyle = 'rgb(76, 175, 80)';
      } else {
        volumeCtx!.fillStyle = 'rgb(240, 240, 240)';
      }
      const xPos = i * (barWidth * 2);

      const height = volumeCanvas.height;
      volumeCtx!.fillRect(xPos, 0, barWidth, height);
    }

    animationFrameIdRef.current = requestAnimationFrame(drawVolumeBars);
  }, [])


  const getMicrophoneInput = React.useCallback(async () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'suspended') {
        setupAudioContext();
        await audioContextRef.current.resume();
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: current } });

      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }

      sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
      sourceRef.current.connect(analyserRef.current);
      analyserRef.current.connect(audioContextRef.current.destination);

      drawVolumeBars();
      console.log('Microphone input started!');

    } catch (err) {
      console.error('Error accessing microphone:', err);
    }
  }, [current, setupAudioContext, drawVolumeBars]);

  const stopMicrophoneInput = React.useCallback(() => {
    if (sourceRef.current) {
      const tracks = sourceRef.current.mediaStream.getTracks();
      tracks.forEach(track => track.stop());
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.suspend();
    }
    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }
    console.log('Microphone input stopped.');
  }, []);

  React.useEffect(() => {
    setupAudioContext();
    setupVolumeBars();

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
      if (sourceRef.current) {
        const tracks = sourceRef.current.mediaStream.getTracks();
        tracks.forEach(track => track.stop());
        sourceRef.current.disconnect();
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().then(() => {
          console.log('AudioContext closed.');
        });
      }
    };
  }, [setupAudioContext, setupVolumeBars]);

  const handleTestInput = async () => {
    if (isTestInput) {
      stopMicrophoneInput();
      setupVolumeBars();
    } else {
      getMicrophoneInput()
    }
    setIsTestInput(!isTestInput);
  };

  const [isTestOutput, setIsTestOutput] = React.useState(false);

  const [currentOutput, setCurrentOutput] = React.useState("default");

  const handleOutputDeviceChange = async (deviceId: string) => { 
    if (isTestOutput) handleTestOutput();
    
    setCurrentOutput(deviceId);
    if (typeof audioRef.current?.setSinkId === "function") {
      await audioRef.current.setSinkId(deviceId);
    }
  };

  const handleTestOutput = async () => {
    if (isTestOutput) {
      audioRef.current!.pause();
      audioRef.current!.currentTime = 0;

      setIsTestOutput(false);
    } else {
      audioRef.current!.play();
      setIsTestOutput(true);
    }
  };

  const audioRef = React.useRef<any>(null);
  return (
    <>
      <Container>
        <div className="mt30">麦克风</div>
        <div>
          <Select
            placeholder="请选择设备"
            onChange={(v) => setCurrent(v)}
            value={current}
            className="cameraSelect"
            disabled={!inputs.length || isTestInput}
          >
            {inputs.map((d) => (
              <Select.Option value={d.deviceId} key={d.deviceId}>
                {formatDeviceLabel(d.label) || d.deviceId}
              </Select.Option>
            ))}
          </Select>

          <Button className="ml10" onClick={handleTestInput}>
            {isTestInput ? "停止测试" : "测试"}
          </Button>
        </div>

        <div>输出</div>
        <canvas
          className="mt10"
          ref={volumeCanvasRef}
          width="350"
          height="20"
          style={{ backgroundColor: 'transparent' }}
        >
        </canvas>

        <div className="mt20">扬声器</div>
        <div>
          <audio ref={audioRef}></audio>
          <Select
            placeholder="请选择设备"
            onChange={(v) => handleOutputDeviceChange(v)}
            value={currentOutput}
            className="cameraSelect"
          >
            {outputs.map((d) => (
              <Select.Option value={d.deviceId} key={d.deviceId}>
                {formatDeviceLabel(d.label) || d.deviceId}
              </Select.Option>
            ))}
          </Select>

          <Button className="ml10" onClick={handleTestOutput}>
            {isTestOutput ? "停止测试" : "测试"}
          </Button>
        </div>
      </Container>
    </>
  );
};

export default AudioSetting;
